import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const LANGUAGES = [
  { code: 'en', label: 'English' }, { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' }, { code: 'ht', label: 'Haitian Creole' },
  { code: 'vi', label: 'Vietnamese' }, { code: 'ar', label: 'Arabic' },
  { code: 'zh', label: 'Chinese Simplified' }, { code: 'pt', label: 'Portuguese' },
  { code: 'ko', label: 'Korean' }, { code: 'ru', label: 'Russian' },
  { code: 'hi', label: 'Hindi' }, { code: 'tl', label: 'Tagalog' },
];

export default function RegisterPage() {
  const { register } = useAuth();
  const { dark } = useTheme();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '', email: '', password: '',
    preferredLanguage: 'en', healthLiteracyLevel: 'medium',
  });
  const [hipaaConsent, setHipaaConsent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 12) return setError('Password must be at least 12 characters');
    if (!/[A-Z]/.test(form.password)) return setError('Password must include an uppercase letter');
    if (!/[a-z]/.test(form.password)) return setError('Password must include a lowercase letter');
    if (!/[0-9]/.test(form.password)) return setError('Password must include a number');
    if (!/[^A-Za-z0-9]/.test(form.password)) return setError('Password must include a special character');
    if (!hipaaConsent) return setError('You must accept the privacy notice to create an account');
    setError('');
    setLoading(true);
    try {
      await register({ ...form, hipaaConsent: 'true' });
      navigate('/app/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-indigo-50 dark:from-slate-950 dark:to-slate-900 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        {/* Back to home */}
        <div className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-sky-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to home
          </Link>
        </div>
        <div className="text-center mb-8">
          <img src={dark ? '/logo-full-dark.svg?v=3' : '/logo-full.svg?v=3'} alt="HealthClear AI" className="h-12 w-auto mx-auto mb-3" />
          <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">Create your free account</p>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Full Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                required
                className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2.5 text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-400"
                placeholder="Maria Gonzalez"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                required
                autoComplete="email"
                className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2.5 text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-400"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Password</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                required
                autoComplete="new-password"
                className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2.5 text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-400"
                placeholder="Min 12 chars, uppercase, lowercase, number, special char"
              />
            </div>

            {/* Language */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Preferred Language</label>
              <select
                value={form.preferredLanguage}
                onChange={(e) => setForm(f => ({ ...f, preferredLanguage: e.target.value }))}
                className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2.5 text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-400"
              >
                {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>

            {/* Literacy */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">How do you prefer explanations?</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'low', label: 'Very simple' },
                  { value: 'medium', label: 'Plain language' },
                  { value: 'high', label: 'More detail' },
                  { value: 'professional', label: 'Medical terms' },
                ].map((lvl) => (
                  <button
                    key={lvl.value}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, healthLiteracyLevel: lvl.value }))}
                    className={`py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                      form.healthLiteracyLevel === lvl.value
                        ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300'
                        : 'border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-500'
                    }`}
                  >
                    {lvl.label}
                  </button>
                ))}
              </div>
            </div>

            {error && <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">{error}</p>}

            {/* HIPAA §164.520 — Privacy Notice Acknowledgment */}
            <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hipaaConsent}
                  onChange={(e) => setHipaaConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-400"
                />
                <span className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                  I acknowledge that HealthClear AI handles protected health information (PHI) and
                  agree to the{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-sky-600 underline">Privacy Policy</a>.
                  Sessions expire after 15 minutes of inactivity per HIPAA security requirements.
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading || !hipaaConsent}
              className="w-full bg-sky-600 hover:bg-sky-700 disabled:bg-sky-300 text-white font-semibold py-2.5 rounded-lg transition-colors"
            >
              {loading ? 'Creating account…' : 'Create account'}
            </button>
          </form>

          <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-4">
            Already have an account?{' '}
            <Link to="/login" className="text-sky-600 font-medium hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
